import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SnapshotRow {
  readonly portalAnalyticsId: string;
  readonly snapshotCode: string;
  readonly metricType: string;
  readonly metricValue: number;
  readonly capturedAt: string;
}

interface Metrics {
  readonly activeUsers: number;
  readonly openRequests: number;
  readonly documentsShared: number;
  readonly communicationsSent: number;
}

const PAGE = 25;

/**
 * M42 §27 PortalAnalyticsDashboard (§15 / §26 KPIs). Read the live engagement
 * metrics and the persisted snapshot history, and capture a new snapshot.
 * Viewing needs M42_ANALYTICS_VIEW.
 */
export function PortalAnalyticsDashboard(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    Promise.all([
      apiRequest<{ data: Metrics }>(`/v1/portal/analytics/metrics`, { token }),
      apiRequest<{ data: SnapshotRow[]; total: number }>(`/v1/portal/analytics/snapshots?${params.toString()}`, { token }),
    ])
      .then(([m, s]) => { if (alive) { setMetrics(m.data); setRows(s.data); setTotal(s.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, page, reload]);

  const capture = async (): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest('/v1/portal/analytics/snapshots', { method: 'POST', body: {}, token });
      setNotice(t('m42.analytics.captured'));
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.analytics.notAuthCapture'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="portal-analytics">
      <h1>{t('m42.analytics.title')}</h1>

      <section aria-label="analytics-kpis">
        <Button variant="primary" type="button" aria-label="capture-snapshot" onClick={() => void capture()}>{t('m42.analytics.captureBtn')}</Button>
        {actionError && <p role="alert" aria-label="analytics-action-error">{actionError}</p>}
        {notice && <p aria-label="analytics-notice">{notice}</p>}
        {metrics && (
          <dl>
            <div><dt>{t('m42.analytics.kpi.activeUsers')}</dt><dd aria-label="kpi-active-users">{metrics.activeUsers}</dd></div>
            <div><dt>{t('m42.analytics.kpi.openRequests')}</dt><dd aria-label="kpi-open-requests">{metrics.openRequests}</dd></div>
            <div><dt>{t('m42.analytics.kpi.documentsShared')}</dt><dd aria-label="kpi-documents-shared">{metrics.documentsShared}</dd></div>
            <div><dt>{t('m42.analytics.kpi.communicationsSent')}</dt><dd aria-label="kpi-communications-sent">{metrics.communicationsSent}</dd></div>
          </dl>
        )}
      </section>

      {error && <p role="alert" aria-label="analytics-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="analytics-total">{t('m42.analytics.total', { count: total })}</p>
          <DataTable
            ariaLabel="analytics-table"
            rows={rows}
            rowKey={(s) => s.portalAnalyticsId}
            emptyMessage={t('m42.analytics.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (s) => `snapshot-code-${s.portalAnalyticsId}`, render: (s) => s.snapshotCode },
              { key: 'metric', header: t('m42.analytics.col.metric'), render: (s) => s.metricType },
              { key: 'value', header: t('m42.analytics.col.value'), render: (s) => s.metricValue },
              { key: 'captured', header: t('m42.analytics.col.captured'), render: (s) => at(s.capturedAt) },
            ]}
          />

          <nav aria-label="analytics-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
